import { ChangeEvent, useCallback } from 'react'
import classNames from 'classnames'
import {
  Input as BaseInput,
  TextArea as BaseTextArea,
  InputProps,
  TextAreaProps,
  SelectProps as BaseSelectProps,
} from '@/atoms/Input'
import { ReactFCC } from '@/types/react'

export const Label: ReactFCC = ({ children, required }) => {
  if (!required) return children

  return (
    <span>
      {children} <span className="text-error-500">*</span>
    </span>
  )
}
export const Input = (props: InputProps) => {
  return (
    <BaseInput
      usePlaceholderAsLabel
      {...props}
      label={<Label required={props.required}>{props.label}</Label>}
      containerClassName={classNames('!mt-0', { 'opacity-50': props.disabled }, props.containerClassName)}
      inputClasses={classNames(
        'photon-paragraph-md border border-b rounded-lg p-2 w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
        props.error ? 'border-error-500' : 'border-core-gray-300',
      )}
      labelClassName="photon-label-sm font-whitney font-medium pb-1.5"
    />
  )
}

export const TextArea = (props: TextAreaProps) => {
  return (
    <BaseTextArea
      usePlaceholderAsLabel
      {...props}
      label={<Label required={props.required}>{props.label}</Label>}
      containerClassName={classNames('!mt-0 -mb-1.5', { 'opacity-50': props.disabled }, props.containerClassName)}
      inputClasses={classNames(
        'photon-paragraph-md border rounded-lg p-2 w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
        props.error ? 'border-error-500' : 'border-core-gray-300',
      )}
      labelClassName="photon-label-sm font-whitney font-medium"
    />
  )
}

interface SelectProps extends Omit<BaseSelectProps, 'onChange'> {
  label: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
  required?: boolean
  containerClassName?: string
}

export const Select = ({
  label,
  options,
  placeholder = 'Select an option...',
  containerClassName,
  disabled,
  onChange,
  ...props
}: SelectProps) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      if (event.currentTarget.value === placeholder) {
        event.currentTarget.value = ''
      }

      onChange(event)
    },
    [onChange, placeholder],
  )

  return (
    <label className={classNames('block', containerClassName, { 'opacity-50': disabled })}>
      <div className="photon-label-sm relative mb-0 pt-0 font-whitney text-sm font-medium duration-300">
        <Label required={props.required}>{label}</Label>
      </div>
      <div className={classNames('my-2 rounded-lg border border-core-gray-300 p-2', props.divClassName ?? '')}>
        <select
          {...props}
          disabled={disabled}
          value={props.value || placeholder}
          onChange={handleChange}
          className={classNames(
            'photon-paragraph-md w-full  !duration-[0ms] focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white hover:cursor-pointer',
            props.selectClassName ?? '',
          )}
        >
          <option value={placeholder} disabled>
            {placeholder}
          </option>
          {options?.map((option) => {
            return (
              <option
                key={typeof option === 'string' ? option : option.value}
                value={typeof option === 'string' ? option : option.value}
              >
                {typeof option === 'string' ? option : option.display}
              </option>
            )
          })}
        </select>
      </div>
    </label>
  )
}
